import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  Loader2,
  FileText,
  Scale,
  History,
  MessageSquare,
  MessageCircle,
  FilePenLine,
  Mail,
  Plus,
  Activity,
} from 'lucide-react'
import { lawyerCasesAPI, legalAPI } from '@/utils/api'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from 'sonner'
import { LegalProcessCard, CaseTimeline, CorrespondenceList, NewActionModal, ActivityTab } from './legal'
import { CaseStatusTab } from './case-status'
import DocumentEditor from '@/components/DocumentEditor'
import {
  CaseHeader,
  CaseDetailsCard,
  NarrativeCard,
  PassengersCard,
  FlightSegmentsCard,
  ClientInfoCard,
  QuickStatsCard,
  DocumentsListCard,
  ExpensesCard,
  LawyerChatTab,
  type CaseData,
} from './case-detail'

interface LawyerCaseDetailProps {
  caseId: number
}

export function LawyerCaseDetail({ caseId }: LawyerCaseDetailProps) {
  const navigate = useNavigate()
  const [caseData, setCaseData] = useState<CaseData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  // Form state
  const [incidentType, setIncidentType] = useState<string>('')
  const [incidentDate, setIncidentDate] = useState<string>('')
  const [airlineCode, setAirlineCode] = useState<string>('')
  const [narrative, setNarrative] = useState<string>('')
  const [customTitle, setCustomTitle] = useState<string>('')

  // Document generation state
  const [generatingDocType, setGeneratingDocType] = useState<string | null>(null)

  // Tab navigation state (controlled for programmatic tab switching)
  const [activeTab, setActiveTab] = useState('status')

  // Action modal state (for pre-selecting action types from Status tab)
  const [actionModalOpen, setActionModalOpen] = useState(false)
  const [preselectedActionType, setPreselectedActionType] = useState<string | undefined>()

  // Unread message count for Chat tab indicator
  const [unreadMessageCount, setUnreadMessageCount] = useState(0)

  // Ref to DocumentEditor for loading generated content
  const documentEditorRef = useRef<{ loadGeneratedContent: (content: any, title: string) => void }>(null)

  const fetchCase = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await lawyerCasesAPI.getCaseById(caseId)
      setCaseData(response.case)
      // Initialize form state
      setIncidentType(response.case.incident_type || '')
      setIncidentDate(response.case.incident_date || '')
      setAirlineCode(response.case.airline_code || '')
      setNarrative(response.case.narrative || '')
      setCustomTitle(response.case.custom_title || '')
      setUnreadMessageCount(response.case.unread_client_message_count || 0)
      setHasChanges(false)
    } catch (err) {
      console.error('Failed to fetch case:', err)
      setError('Fall konnte nicht geladen werden. Bitte erneut versuchen.')
    } finally {
      setIsLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    fetchCase()
  }, [fetchCase])

  // Track changes
  useEffect(() => {
    if (!caseData) return
    const changed =
      incidentType !== (caseData.incident_type || '') ||
      incidentDate !== (caseData.incident_date || '') ||
      airlineCode !== (caseData.airline_code || '') ||
      narrative !== (caseData.narrative || '') ||
      customTitle !== (caseData.custom_title || '')
    setHasChanges(changed)
  }, [incidentType, incidentDate, airlineCode, narrative, customTitle, caseData])

  const handleSave = async () => {
    if (!caseData) return
    setIsSaving(true)
    try {
      await lawyerCasesAPI.updateCase(caseId, {
        incident_type: incidentType || null,
        incident_date: incidentDate || null,
        airline_code: airlineCode || null,
        narrative: narrative || null,
        custom_title: customTitle || '',
      })
      toast.success('Fall erfolgreich gespeichert')
      await fetchCase()
    } catch (err) {
      console.error('Failed to save case:', err)
      toast.error('Fall konnte nicht gespeichert werden. Bitte erneut versuchen.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await lawyerCasesAPI.deleteCase(caseId)
      toast.success('Fall erfolgreich gelöscht')
      navigate({ to: '/lawyer/cases' })
    } catch (err) {
      console.error('Failed to delete case:', err)
      toast.error('Fall konnte nicht gelöscht werden. Bitte erneut versuchen.')
      setIsDeleting(false)
    }
  }

  const handleMessagesRead = useCallback(() => {
    setUnreadMessageCount(0)
  }, [])

  const handleGenerateDocument = async (templateType: string) => {
    setGeneratingDocType(templateType)
    try {
      const response = await legalAPI.generateDocument(caseId, templateType)
      if (response.status === 'success') {
        // Switch to documents tab
        setActiveTab('documents')

        // Load content into editor after tab switch
        setTimeout(() => {
          documentEditorRef.current?.loadGeneratedContent(
            response.content_json,
            response.template.name
          )
        }, 50)

        toast.success(`${response.template.name} bereit zur Bearbeitung`)
      } else {
        toast.error('Dokument konnte nicht generiert werden')
      }
    } catch (err) {
      console.error('Failed to generate document:', err)
      toast.error('Fehler bei der Dokumentgenerierung')
    } finally {
      setGeneratingDocType(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-slate-400 animate-spin mb-4" />
        <p className="text-slate-500">Fall wird geladen...</p>
      </div>
    )
  }

  if (error || !caseData) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <FileText className="h-16 w-16 text-red-300 mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Fehler</h3>
        <p className="text-slate-500 text-center max-w-md mb-4">{error || 'Fall nicht gefunden'}</p>
        <Button onClick={() => navigate({ to: '/lawyer/cases' })}>Zurück zu Fällen</Button>
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <CaseHeader
        caseData={caseData}
        hasChanges={hasChanges}
        isSaving={isSaving}
        isDeleting={isDeleting}
        onSave={handleSave}
        onDelete={handleDelete}
        onBack={() => navigate({ to: '/lawyer/cases' })}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="status" className="gap-2">
            <Activity className="h-4 w-4" />
            Status
          </TabsTrigger>
          <TabsTrigger value="overview" className="gap-2">
            <FileText className="h-4 w-4" />
            Übersicht
          </TabsTrigger>
          <TabsTrigger value="legal" className="gap-2">
            <Scale className="h-4 w-4" />
            Verfahren
          </TabsTrigger>
          <TabsTrigger value="correspondence" className="gap-2">
            <MessageSquare className="h-4 w-4" />
            Korrespondenz
          </TabsTrigger>
          <TabsTrigger value="chat" className="gap-2 relative">
            <MessageCircle className="h-4 w-4" />
            Chat
            {unreadMessageCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 text-[10px] text-white font-medium items-center justify-center">
                  {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                </span>
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="timeline" className="gap-2">
            <History className="h-4 w-4" />
            Verlauf
          </TabsTrigger>
          <TabsTrigger value="documents" className="gap-2">
            <FilePenLine className="h-4 w-4" />
            Dokumente
          </TabsTrigger>
          <TabsTrigger value="activity" className="gap-2">
            <History className="h-4 w-4" />
            Aktivität
          </TabsTrigger>
        </TabsList>

        {/* Status Tab - Default */}
        <TabsContent value="status" className="space-y-0">
          <CaseStatusTab
            caseId={caseData.id}
            aiSummary={caseData.ai_summary}
            defendantAirlineCode={caseData.defendant_airline_code}
            defendantAirlineName={caseData.defendant_airline_name}
            onGenerateDocument={handleGenerateDocument}
            onOpenActionModal={(preselected) => {
              setPreselectedActionType(preselected)
              setActionModalOpen(true)
            }}
            generatingDocType={generatingDocType}
          />
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-0">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - 2/3 width */}
            <div className="lg:col-span-2 space-y-6">
              <CaseDetailsCard
                incidentType={incidentType}
                incidentDate={incidentDate}
                airlineCode={airlineCode}
                customTitle={customTitle}
                summaryTitle={caseData.summary_title}
                onChangeIncidentType={setIncidentType}
                onChangeIncidentDate={setIncidentDate}
                onChangeAirlineCode={setAirlineCode}
                onChangeCustomTitle={setCustomTitle}
              />

              <NarrativeCard narrative={narrative} onChange={setNarrative} />

              <PassengersCard
                caseId={caseId}
                passengers={caseData.passengers}
                onUpdate={fetchCase}
              />

              <FlightSegmentsCard
                caseId={caseId}
                segments={caseData.flight_segments}
                onUpdate={fetchCase}
              />
            </div>

            {/* Right Column - 1/3 width */}
            <div className="space-y-6">
              <ClientInfoCard
                clientName={caseData.client_name}
                clientEmail={caseData.client_email}
                createdAt={caseData.created_at}
              />

              <QuickStatsCard
                documentCount={caseData.document_count}
                expenseCount={caseData.expense_count}
                passengerCount={caseData.passenger_count}
                flightSegmentCount={caseData.flight_segment_count}
              />

              <DocumentsListCard documents={caseData.documents} onUpdate={fetchCase} />

              <ExpensesCard expenses={caseData.expenses} />
            </div>
          </div>
        </TabsContent>

        {/* Legal Process Tab */}
        <TabsContent value="legal" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <LegalProcessCard caseId={caseData.id} />
            </div>
            <div className="space-y-6">
              {/* Document Generation */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <FilePenLine className="h-5 w-5 text-slate-400" />
                  Dokumente erstellen
                </h2>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    onClick={() => handleGenerateDocument('mahnschreiben')}
                    disabled={generatingDocType === 'mahnschreiben'}
                  >
                    {generatingDocType === 'mahnschreiben' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                    Mahnschreiben
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    onClick={() => handleGenerateDocument('rsv_request')}
                    disabled={generatingDocType === 'rsv_request'}
                  >
                    {generatingDocType === 'rsv_request' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4" />
                    )}
                    Deckungszusage
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    onClick={() => handleGenerateDocument('klage')}
                    disabled={generatingDocType === 'klage'}
                  >
                    {generatingDocType === 'klage' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Scale className="h-4 w-4" />
                    )}
                    Klage
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    onClick={() => handleGenerateDocument('replik')}
                    disabled={generatingDocType === 'replik'}
                  >
                    {generatingDocType === 'replik' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <MessageSquare className="h-4 w-4" />
                    )}
                    Replik
                  </Button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Plus className="h-5 w-5 text-slate-400" />
                  Schnellaktionen
                </h2>
                <div className="space-y-2">
                  <NewActionModal caseId={caseData.id} onActionCreated={() => {}} />
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Correspondence Tab */}
        <TabsContent value="correspondence" className="space-y-6">
          <CorrespondenceList caseId={caseData.id} />
        </TabsContent>

        {/* Chat Tab */}
        <TabsContent value="chat" className="space-y-6">
          <LawyerChatTab caseId={caseData.id} onMessagesRead={handleMessagesRead} />
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <History className="h-5 w-5 text-slate-400" />
                Fallverlauf
              </h2>
              <NewActionModal caseId={caseData.id} onActionCreated={() => {}} />
            </div>
            <CaseTimeline caseId={caseData.id} />
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-6">
          <DocumentEditor ref={documentEditorRef} caseId={caseData.id} locale="de" embedded />
        </TabsContent>

        {/* Activity Tab - History of changes */}
        <TabsContent value="activity" className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <History className="h-5 w-5 text-slate-400" />
              Änderungsverlauf
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Protokoll aller wichtigen Änderungen an diesem Fall
            </p>
            <ActivityTab caseId={caseData.id} />
          </div>
        </TabsContent>
      </Tabs>

      {/* Controlled Action Modal for Status tab next steps */}
      <NewActionModal
        caseId={caseData.id}
        open={actionModalOpen}
        onOpenChange={setActionModalOpen}
        defaultActionType={preselectedActionType}
        hideTrigger
        onActionCreated={() => {
          setActionModalOpen(false)
          setPreselectedActionType(undefined)
        }}
      />
    </div>
  )
}
